import { useEffect, useState, useMemo, forwardRef, useRef, useCallback } from 'react';
import { Autocomplete, TextField, Box, Typography, Popper, Chip } from '@mui/material';
import { debounce } from '@mui/material/utils';
import CircularProgress from '@mui/material/CircularProgress';
import { sortBy, throttle } from 'lodash';

const CustomPopper = ({ children, ...props }) => (
  <Popper
    {...props}
    sx={{
      zIndex: 9999,
      backgroundColor: '#ffffff',
      boxShadow:
        '0px 5px 5px -3px rgba(145, 158, 171, 0.2),0px 8px 10px 1px rgba(145, 158, 171, 0.14),0px 3px 14px 2px rgba(145, 158, 171, 0.12)',
    }}
  >
    {children}
  </Popper>
);

export const AutocompleteField = forwardRef(
  (
    {
      id,
      label,
      size = 'small',
      value,
      onChange = () => {},
      optionsData = [],
      optionName = 'name',
      backendSearch = () => {},
      fetchData = () => {},
      hasMore = false,
      loading = false,
      multiple = false,
      disabled = false,
      error = '',
      ...props
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState('');
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const listboxRef = useRef();

    const sortedOptions = useMemo(() => sortBy(options, ['name']), [options]);
    const debouncedSearch = useMemo(() => debounce(backendSearch, 1000), [backendSearch]);

    // Throttled fetch to avoid spamming
    const throttledFetchData = useMemo(() => {
      return throttle(() => {
        if (!loading && hasMore) {
          fetchData(inputValue);
        }
      }, 500);
    }, [fetchData, loading, hasMore, inputValue]);

    const handleScroll = useCallback(
      (event) => {
        const listboxNode = event.currentTarget;
        const bottomReached = listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 50;
        if (bottomReached) {
          throttledFetchData();
        }
      },
      [throttledFetchData]
    );

    useEffect(() => {
      setOptions(optionsData);
    }, [optionsData]);

    useEffect(() => {
      if (!multiple && typeof value === 'object' && value?.name) {
        setInputValue(value.name);
      } else if (!multiple && !value) {
        setInputValue('');
      }
    }, [value, multiple]);

    const handleUnselectOption = (item) => {
      const updatedSelectedOptions = value.filter((option) => option.id !== item.id);
      onChange(updatedSelectedOptions);
    };

    return (
      <>
        <Autocomplete
          id={id}
          multiple={multiple}
          disabled={disabled}
          filterSelectedOptions
          freeSolo
          open={open}
          value={value || []}
          onChange={(_, selectedValue) => onChange(selectedValue)}
          inputValue={inputValue}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'input') {
              setInputValue(newInputValue);
              debouncedSearch(newInputValue);
            } else if (reason === 'clear') {
              setInputValue('');
              onChange(multiple ? [] : null); // Clear the external value too
            }
          }}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          isOptionEqualToValue={(option, val) => option?.name === val?.name}
          getOptionLabel={(option) => option?.name || ''}
          filterOptions={(x) => x}
          options={sortedOptions}
          loading={loading}
          renderTags={() => {}}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              key={option.id}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 400, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
              >
                {option[optionName]}
              </Typography>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              ref={ref}
              {...params}
              label={label}
              size={size}
              autoComplete="list"
              error={!!error}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && (
                      <CircularProgress
                        color="inherit"
                        size={20}
                      />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          PopperComponent={CustomPopper}
          ListboxProps={{
            onScroll: handleScroll,
            ref: listboxRef,
            style: { maxHeight: 185, overflowY: 'auto' },
          }}
          {...props}
        />
        {value?.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', py: 2 }}>
            {value.map((item) => (
              <Chip
                key={item.id}
                label={item.name}
                variant="contained"
                size="small"
                onDelete={() => handleUnselectOption(item)}
                sx={{ backgroundColor: '#d9d9d9', '& .MuiChip-deleteIcon': { color: '#404041' } }}
              />
            ))}
          </Box>
        )}
      </>
    );
  }
);

AutocompleteField.displayName = 'AutocompleteField';
